import { Helmet } from "react-helmet-async";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface BenchmarkRow {
  [key: string]: string | number;
}

export interface BenchmarkTableProps {
  title: string;
  description?: string;
  source?: string;
  sourceYear?: string;
  columns: {
    key: string;
    label: string;
    highlight?: boolean;
  }[];
  data: BenchmarkRow[];
  highlightColumn?: string;
}

export const BenchmarkTable = ({
  title,
  description,
  source,
  sourceYear,
  columns,
  data,
}: BenchmarkTableProps) => {
  // Generate JSON-LD Dataset schema for AI/search engine discoverability
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: title,
    description: description || `Benchmark data for ${title}`,
    ...(source && {
      creator: {
        "@type": "Organization",
        name: source,
      },
    }),
    ...(sourceYear && { temporalCoverage: sourceYear }),
    distribution: {
      "@type": "DataDownload",
      encodingFormat: "text/html",
      contentUrl: typeof window !== "undefined" ? window.location.href : "",
    },
    variableMeasured: columns.map((col) => ({
      "@type": "PropertyValue",
      name: col.label,
      description: `${col.label} metric from ${title}`,
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="bg-muted/50 px-4 py-3 border-b border-border">
          <h3 className="font-display font-semibold text-foreground text-sm sm:text-base">
            {title}
          </h3>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {columns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={`text-xs font-medium ${
                      col.highlight ? "text-primary" : "text-muted-foreground"
                    }`}
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, rowIndex) => (
                <TableRow key={rowIndex} className="hover:bg-muted/30">
                  {columns.map((col, colIndex) => (
                    <TableCell
                      key={col.key}
                      className={`text-sm ${
                        colIndex === 0
                          ? "font-medium text-foreground"
                          : "text-muted-foreground"
                      } ${col.highlight ? "text-primary font-semibold" : ""}`}
                    >
                      {row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {source && (
          <div className="px-4 py-2 border-t border-border bg-muted/30">
            <p className="text-[10px] text-muted-foreground">
              Source: {source}
              {sourceYear && ` (${sourceYear})`}
            </p>
          </div>
        )}
      </div>
    </>
  );
};
